/**
 * The goals scoreboard at the top of /team — one row per active goal, the
 * current value against where the calendar says it should be.
 *
 * A table, not charts: the question it answers is "is the machine on pace",
 * and a number against an expectation answers it faster than a bar. Goals
 * are DB-canonical (`GET /api/v1/admin/goals`, wire shape from
 * `system/services/goals.py`); authoring stays on the confirmation-gated
 * `goal_*` MCP tools, so this surface only reads.
 */
import { Link } from "react-router-dom";

import { adminApi, type AdminGoal } from "../../api/admin";
import { Pill } from "../../components/Pill";
import { ShortId } from "../../components/ShortId";
import { agentDrawerUrl, goalPaceTone } from "../../lib/aiTeam";

/** Wire values are floats (200.0); whole numbers read as integers. */
function fmtValue(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

export function GoalsScoreboard() {
  const goalsQuery = adminApi.aiTeam.goals();
  const goals = goalsQuery.data;

  // No data yet — loading, or the endpoint isn't deployed (`retry: false`
  // keeps that cheap). Either way the scoreboard is simply absent; an error
  // strip here would shout about infrastructure on a page about the team.
  if (!goals) return null;

  return (
    <section className="panel panel--flush mb-5 overflow-x-auto">
      <table className="table">
        <thead>
          <tr>
            <th>Goal</th>
            <th>Owner</th>
            <th>Period</th>
            <th className="text-right">Target</th>
            <th className="text-right">Current</th>
            <th>Pace</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {goals.length === 0 ? (
            <tr>
              <td colSpan={7} className="text-sm text-ink-low">
                No goals configured.
              </td>
            </tr>
          ) : (
            goals.map((goal) => <GoalRow key={goal.id} goal={goal} />)
          )}
        </tbody>
      </table>
    </section>
  );
}

function GoalRow({ goal }: { goal: AdminGoal }) {
  const pace = goal.pace;
  const statusWord =
    goal.measurement === "unavailable" || !pace
      ? "no data"
      : pace.on_track
        ? "on pace"
        : "behind";

  return (
    <tr>
      <td>
        <div className="text-sm font-medium text-ink">{goal.title}</div>
        <div className="text-xs text-ink-low">
          <ShortId id={goal.id} />
          {goal.metric_key ? <span className="font-mono"> · {goal.metric_key}</span> : null}
        </div>
      </td>
      <td>
        {goal.assigned_agent_id ? (
          <Link
            className="font-mono text-xs link-quiet"
            to={agentDrawerUrl(goal.assigned_agent_id)}
          >
            {goal.assigned_agent_slug ?? goal.assigned_agent_id.slice(0, 8)}
          </Link>
        ) : (
          <span className="text-sm text-ink-low">team</span>
        )}
      </td>
      <td className="text-sm text-ink-medium tnum" title={goal.period}>
        {goal.window.label}
      </td>
      <td className="text-sm text-ink tnum text-right">
        {fmtValue(goal.target_value)}
        {goal.unit ? (
          <span className="text-xs text-ink-low"> {goal.unit}</span>
        ) : null}
      </td>
      <td className="text-sm text-ink tnum text-right">
        {goal.current_value != null ? fmtValue(goal.current_value) : "—"}
      </td>
      <td className="text-sm text-ink-medium tnum">
        {pace && goal.current_value != null
          ? `${fmtValue(goal.current_value)} / ${fmtValue(pace.expected_now)} expected`
          : "—"}
      </td>
      <td>
        <Pill
          value={statusWord}
          tone={goalPaceTone(goal.measurement, pace?.on_track)}
        />
      </td>
    </tr>
  );
}
